import React, {
  createContext,
  type FC,
  useContext,
  useMemo,
  useState,
} from "react";

import { usePrimeSubscriptionModalUI } from "~/clientState/providers/PrimeSubscriptionModalProvider";
import type { BENEFIT_TRACKING } from "~/components/acquisition/Lightbox/LightboxRevamp/config/tracking";

interface PrimeLightboxAccordionProviderProps {
  initialAccordionState?: BENEFIT_TRACKING;
}

interface GlobalContent {
  accordionToOpen?: BENEFIT_TRACKING;
  setAccordionToOpen: (param: BENEFIT_TRACKING | undefined) => void;
}

export interface PrimeLightboxAccordionReturn {
  accordionToOpen: BENEFIT_TRACKING | undefined;
  openWithAccordion: (param: BENEFIT_TRACKING | undefined) => void;
  setAccordionToOpen: (param: BENEFIT_TRACKING | undefined) => void;
}

export const PrimeLightboxAccordionContext = createContext<GlobalContent>({
  accordionToOpen: undefined,
  setAccordionToOpen: () => undefined,
});

const PrimeLightboxAccordionProvider: FC<
  PrimeLightboxAccordionProviderProps
> = ({ children, initialAccordionState }) => {
  const [accordionToOpen, setAccordionToOpen] = useState(initialAccordionState);

  const value = useMemo(
    () => ({ accordionToOpen, setAccordionToOpen }),
    [accordionToOpen, setAccordionToOpen],
  );

  return (
    <PrimeLightboxAccordionContext.Provider value={value}>
      {children}
    </PrimeLightboxAccordionContext.Provider>
  );
};

export function usePrimeLightboxAccordion(): PrimeLightboxAccordionReturn {
  const context = useContext(PrimeLightboxAccordionContext);
  const { open } = usePrimeSubscriptionModalUI();

  const accordionToOpen = context.accordionToOpen;
  const setAccordionToOpen = context.setAccordionToOpen;
  const openWithAccordion = (accordion: BENEFIT_TRACKING | undefined): void => {
    setAccordionToOpen(accordion);
    open();
  };

  return {
    accordionToOpen,
    openWithAccordion,
    setAccordionToOpen,
  };
}

export default PrimeLightboxAccordionProvider;
